import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Plus,
  Trash2,
  PanelLeftClose,
  PanelLeft,
  Workflow,
  Loader2,
  Zap,
  MoreHorizontal,
  Pencil,
  Star,
  Share2,
  Globe,
  ListChecks,
  HelpCircle,
  Store,
  Users,
  PlayCircle,
  ChevronsUp,
  ChevronsDown,
  ChevronDown,
  ChevronRight,
  GraduationCap,
} from "lucide-react";
import { Button } from "@dashai/components/ui/button";
import { ScrollArea } from "@dashai/components/ui/scroll-area";
import { Separator } from "@dashai/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@dashai/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@dashai/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTitle } from "@dashai/components/ui/sheet";
import { useIsNarrowViewport } from "@dashai/hooks/useIsNarrowViewport";
import { ChatSession } from "@dashai/types/dashai";
import { byLatestMessage, chatDisplayTitle, editableChatTitle } from "@dashai/lib/toChatSession";
import { cn } from "@dashai/lib/utils";
import { UserProfileDropdown } from "./UserProfileDropdown";
import { useWorkspaceListStatus } from "@/hooks/useWorkspaceListStatus";
import { useWorkflowsLabel } from "@/hooks/useWorkspaceUiConfig";
import { PersonaCard } from "./PersonaCard";
import { usePersonaVisibility } from "@dashai/hooks/usePersonaVisibility";
import { DASHBOARD_ICONS, launchBrowseEntry } from "@dashai/lib/browseNavigation";
import type { LaunchWorkflow } from "@dashai/lib/browseNavigation";
import { groupSfaVariants } from "@dashai/utils/sfaVariants";
import type { BrowseEntry } from "@dashai/utils/sfaVariants";
import type { DashboardCard } from "@/data/dashboardCards";
import type { WorkflowAgent } from "./WorkflowAgentsGrid";

const CHATS_EXPANDED_KEY = "dashai-chats-expanded";
// Per-franchise SFA entries: above the flat cap the sidebar shows one
// expandable group row instead of a row per franchise.
const SFA_GROUP_EXPANDED_KEY = "dashai-sfa-group-expanded";

export interface ChatSidebarProps {
  sessions: ChatSession[];
  currentChatId: string | null;
  isCollapsed: boolean;
  profileMemoryOpen?: boolean;
  autonomousAgentOpen?: boolean;
  autonomousAutoMode?: boolean;
  accountProfileOpen?: boolean;
  hasMoreSessions?: boolean;
  sessionsLoading?: boolean;
  sessionsError?: boolean;
  // User profile props
  userName?: string;
  userEmail?: string;
  userPhotoUrl?: string;
  allowedClients?: string[];
  currentClient?: string | null;
  onToggleCollapse: () => void;
  onNewChat: () => void;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onDeleteAllChats?: () => void;
  onRenameChat?: (chatId: string, newTitle: string) => void;
  onToggleStar?: (chatId: string) => void;
  onShareChat?: (chatId: string) => void;
  onProfileMemory?: () => void;
  onSavedCanvas?: () => void;
  onAutonomousAgent?: () => void;
  onScheduledReports?: () => void;
  onAccountProfile?: () => void;
  // Supplied inside DashAI; absent on analytics pages. See LaunchWorkflow.
  onLaunchWorkflow?: LaunchWorkflow;
  onAdminPanel?: () => void;
  onLoadMoreSessions?: () => void;
  onRetrySessions?: () => void;
  onClientChange?: (client: string) => void;
  onSignOut?: () => void;
  isAdmin?: boolean;
  // Staff-only switcher visibility: pinned workspaces plus focus mode.
  isStaff?: boolean;
  // Whether to show the User Management / Settings nav items — computed by the
  // parent (which holds the Redux auth data) from the same predicate as the
  // route guard, so the menu never offers a link that redirects to "/".
  canAccessUserManagement?: boolean;
  pinnedWorkspaces?: string[];
  focusMode?: boolean;
  onTogglePinWorkspace?: (client: string) => void;
  onToggleFocusMode?: () => void;
}

export function ChatSidebar({
  sessions,
  currentChatId,
  isCollapsed,
  // profileMemoryOpen / accountProfileOpen stay declared on the props above
  // for the callers that pass them, but this sidebar no longer renders a nav
  // row for either surface — both moved to the account dropdown — so there is
  // nothing here to highlight and destructuring them would be dead.
  autonomousAgentOpen = false,
  autonomousAutoMode = false,
  hasMoreSessions = false,
  sessionsLoading = false,
  sessionsError = false,
  userName = "",
  userEmail = "",
  userPhotoUrl,
  allowedClients = [],
  currentClient = null,
  onToggleCollapse,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onDeleteAllChats,
  onRenameChat,
  onToggleStar,
  onShareChat,
  onProfileMemory,
  onSavedCanvas,
  onAutonomousAgent,
  onScheduledReports,
  onAccountProfile,
  onLaunchWorkflow,
  onAdminPanel,
  onLoadMoreSessions,
  onRetrySessions,
  onClientChange,
  onSignOut,
  isAdmin = false,
  isStaff = false,
  canAccessUserManagement = false,
  pinnedWorkspaces = [],
  focusMode = false,
  onTogglePinWorkspace,
  onToggleFocusMode,
}: ChatSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // Persona-filtered view of the workspace's entitled dashboards + workflows.
  // Entitlement (visibility registry, product_scopes, per-user Global Key
  // Accounts probe) is resolved inside the hook, so this component calls no
  // individual gate hook of its own. The sidebar renders only the in-persona
  // sets. Browsing the rest of the entitlement lives on the home page's
  // Show all.
  const personaVisibility = usePersonaVisibility(currentClient ?? null);
  const workflowsLabel = useWorkflowsLabel();
  const workspaceListStatus = useWorkspaceListStatus();
  // Below `lg` a persistent 260px sidebar takes most of the window, which left
  // the dashboards a 115px content column on a phone. There the sidebar renders
  // as the icon rail and its expanded form opens over the content instead.
  const isNarrow = useIsNarrowViewport();
  // Its own state, so an automatic collapse never overwrites the user's stored
  // desktop preference. Mirrors `openMobile` in components/ui/sidebar.tsx.
  const [narrowOverlayOpen, setNarrowOverlayOpen] = useState(false);
  const [logoHovered, setLogoHovered] = useState(false);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  const handleToggleCollapse = useCallback(() => {
    if (isNarrow) setNarrowOverlayOpen((open) => !open);
    else onToggleCollapse();
  }, [isNarrow, onToggleCollapse]);

  // Tapping a nav entry in the overlay navigates, so the overlay has done its
  // job and should get out of the way.
  useEffect(() => {
    setNarrowOverlayOpen(false);
  }, [location.pathname]);

  // Expand/collapse state for CHATS section — hides DASHBOARDS when expanded
  const [isChatsExpanded, setIsChatsExpanded] = useState(() => {
    try {
      return localStorage.getItem(CHATS_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleChatsExpanded = useCallback(() => {
    setIsChatsExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(CHATS_EXPANDED_KEY, String(next));
      } catch {
        // Storage refuses in private mode and past quota; the state still holds for this visit.
      }
      return next;
    });
  }, []);

  // Expand/collapse for the grouped per-franchise SFA entries,
  // rendered only when the pairs exceed the flat cap. Same persistence
  // pattern as the CHATS toggle.
  const [isSfaGroupExpanded, setIsSfaGroupExpanded] = useState(() => {
    try {
      return localStorage.getItem(SFA_GROUP_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleSfaGroupExpanded = useCallback(() => {
    setIsSfaGroupExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SFA_GROUP_EXPANDED_KEY, String(next));
      } catch {
        // Storage refuses in private mode and past quota; the state still holds for this visit.
      }
      return next;
    });
  }, []);

  // Split sessions into starred and regular
  const starredSessions = useMemo(
    () => sessions.filter((s) => s.is_starred).sort(byLatestMessage),
    [sessions]
  );
  const regularSessions = useMemo(
    () => sessions.filter((s) => !s.is_starred).sort(byLatestMessage),
    [sessions]
  );

  // Auto-focus rename input
  useEffect(() => {
    if (editingChatId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingChatId]);

  // Inline rename handlers
  const startRename = useCallback((session: ChatSession) => {
    setEditingChatId(session.chat_id);
    setEditTitle(editableChatTitle(session));
  }, []);

  const confirmRename = useCallback(() => {
    if (editingChatId && editTitle.trim()) {
      onRenameChat?.(editingChatId, editTitle.trim());
    }
    setEditingChatId(null);
    setEditTitle("");
  }, [editingChatId, editTitle, onRenameChat]);

  const cancelRename = useCallback(() => {
    setEditingChatId(null);
    setEditTitle("");
  }, []);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;
      if (nearBottom && hasMoreSessions && !sessionsLoading) {
        onLoadMoreSessions?.();
      }
    },
    [hasMoreSessions, sessionsLoading, onLoadMoreSessions]
  );

  // Nav item shared by the persona-filtered DASHBOARDS / WORKFLOWS lists.
  //
  // `collapsed` is a parameter rather than the `isCollapsed` prop because the
  // sidebar renders TWICE in one pass at narrow widths — the rail in flow and
  // the expanded form in the overlay — and only the caller knows which one this
  // item belongs to. A helper closing over the prop rendered full labels into
  // the icon-only rail.
  const renderNavItem = (
    collapsed: boolean,
    key: string,
    Icon: React.ElementType,
    label: string,
    isActive: boolean,
    onClick: () => void,
    trailing?: React.ReactNode
  ) => (
    <Button
      key={key}
      variant="ghost"
      onClick={onClick}
      className={cn(
        "w-full justify-start mt-0.5",
        collapsed ? "h-10 w-10 p-0 justify-center" : "h-9 px-3 gap-3",
        isActive
          ? "bg-sidebar-accent text-sidebar-foreground"
          : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed && <span className="text-[14.5px] truncate">{label}</span>}
      {!collapsed && trailing}
    </Button>
  );

  // Chip styling note: the `.dashai-scope` wildcard border reset overrides
  // border-* utilities on the dark sidebar, so the chips rely on background
  // contrast only.
  const renderSidebarChip = (text: string) => (
    <span className="ml-auto shrink-0 rounded-full bg-sidebar-accent px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-sidebar-foreground/60">
      {text}
    </span>
  );

  // Both persona lists read empty while the visibility registry is in flight, so
  // keying the empty copy off length alone states the persona is empty about a
  // set that has not resolved. Each section therefore renders whatever has
  // resolved first, falls back to these stand-in rows only while nothing has
  // and status is still loading, and reserves the empty copy for a settled
  // empty set. Module-gated dashboards resolve ahead of the registry, so this
  // keeps them visible rather than hiding them behind a spinner — the same
  // order the home grid uses.
  const renderSectionSkeleton = (collapsed: boolean, rows: number) =>
    Array.from({ length: rows }, (_, i) => (
      <div
        key={`skeleton-${i}`}
        aria-hidden="true"
        className={cn(
          "mt-0.5 animate-pulse rounded-md bg-sidebar-accent/50",
          collapsed ? "h-10 w-10" : "h-9 w-full"
        )}
      />
    ));

  const renderDashboardItems = (collapsed: boolean, cards: DashboardCard[]) =>
    cards.map((card) =>
      renderNavItem(
        collapsed,
        card.id,
        DASHBOARD_ICONS[card.icon] ?? Globe,
        card.label,
        location.pathname.startsWith(card.route),
        () => navigate(card.route)
      )
    );

  const launchEntry = useCallback(
    (entry: BrowseEntry) => launchBrowseEntry(entry, onLaunchWorkflow, navigate),
    [onLaunchWorkflow, navigate]
  );

  const renderWorkflowItems = (collapsed: boolean, entries: BrowseEntry[]) =>
    entries.map((entry) =>
      // `kind` has to be narrowed before base/prefill are reachable, so a
      // variant cannot be launched by its own non-catalogue id by mistake.
      entry.kind === "variant"
        ? renderNavItem(
            collapsed,
            entry.agent.id,
            entry.agent.outputIcon ?? Workflow,
            entry.agent.name,
            false,
            () => launchEntry(entry),
            renderSidebarChip("Prefilled")
          )
        : renderNavItem(
            collapsed,
            entry.agent.id,
            entry.agent.outputIcon ?? Workflow,
            entry.agent.name,
            false,
            () => launchEntry(entry)
          )
    );

  // The workflows list with the >cap grouping applied: many per-franchise
  // entries collapse into one expandable "Single Franchise Audit (N)" row
  // with market-tagged sub-entries, so a long franchise list cannot flood
  // the section. Collapsed sidebar renders flat icon buttons — there is no
  // grouping idiom without labels.
  const renderWorkflowSection = (collapsed: boolean, entries: BrowseEntry[]) => {
    const group = collapsed ? null : groupSfaVariants(entries);
    if (!group) {
      return renderWorkflowItems(collapsed, entries);
    }
    const { before, variants, after } = group;
    return (
      <>
        {renderWorkflowItems(collapsed, before)}
        <Button
          key="sfa-variant-group"
          variant="ghost"
          onClick={toggleSfaGroupExpanded}
          className="w-full justify-start mt-0.5 h-9 px-3 gap-3 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <Store className="h-[18px] w-[18px] shrink-0" />
          <span className="text-[14.5px] truncate">Single Franchise Audit</span>
          <span className="ml-auto flex items-center gap-1 text-[12px] text-sidebar-foreground/50">
            ({variants.length})
            {isSfaGroupExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </span>
        </Button>
        {isSfaGroupExpanded &&
          variants.map((entry) => (
            <Button
              key={entry.agent.id}
              variant="ghost"
              onClick={() => launchEntry(entry)}
              className="w-full justify-start mt-0.5 h-8 pl-9 pr-3 gap-2 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
            >
              <span className="text-[13.5px] truncate">{entry.prefill.franchise}</span>
              {renderSidebarChip(entry.prefill.country)}
            </Button>
          ))}
        {renderWorkflowItems(collapsed, after)}
      </>
    );
  };

  const renderChatItem = (session: ChatSession) => {
    const isEditing = editingChatId === session.chat_id;
    const displayTitle = chatDisplayTitle(session);

    return (
      <div
        key={session.chat_id}
        className={cn(
          "group relative flex items-center gap-1.5 rounded-lg cursor-pointer transition-colors px-3 py-1.5 pr-8 overflow-hidden",
          currentChatId === session.chat_id
            ? "bg-sidebar-accent text-sidebar-foreground"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
        )}
        onClick={() => !isEditing && onSelectChat(session.chat_id)}
      >
        {/* Star icon - always visible when starred */}
        {session.is_starred && (
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 shrink-0" />
        )}

        {isEditing ? (
          <input
            ref={editInputRef}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmRename();
              if (e.key === "Escape") cancelRename();
            }}
            onBlur={confirmRename}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-[13px] bg-transparent border border-sidebar-border rounded px-1 py-0 outline-none focus:border-primary min-w-0"
            maxLength={100}
          />
        ) : (
          <span className="flex-1 truncate text-[14px] flex items-center gap-1.5 min-w-0 overflow-hidden">
            <span className="truncate">{displayTitle}</span>
          </span>
        )}

        {/* Three-dot menu - absolute positioned */}
        {!isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-sidebar-accent/80"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4 text-sidebar-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="right"
              className="w-40 z-[100] bg-popover border border-border shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleStar?.(session.chat_id);
                }}
                className="cursor-pointer"
              >
                <Star
                  className={cn(
                    "h-4 w-4 mr-2",
                    session.is_starred && "fill-yellow-500 text-yellow-500"
                  )}
                />
                {session.is_starred ? "Unstar" : "Star"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  startRename(session);
                }}
                className="cursor-pointer"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onShareChat?.(session.chat_id);
                }}
                className="cursor-pointer"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingChatId(session.chat_id);
                }}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  };

  // At narrow widths the sidebar renders twice in one pass: the rail in flow,
  // and the expanded form in the overlay. The parameter shadows the
  // `isCollapsed` prop so the JSX written inline below reads the flag it is
  // rendered with. That shadowing reaches inline JSX only, which is why the
  // list helpers above take the flag as an argument — a helper defined outside
  // this function closes over the PROP, and one that did rendered full labels
  // into the icon-only rail.
  const renderSidebar = (isCollapsed: boolean) => (
    <div
      className={cn(
        // `overflow-hidden` is what keeps the profile footer on screen. Without
        // it a column whose children exceed `h-full` grows past it, and the
        // footer — the last child — leaves the viewport instead of pinning.
        "h-full bg-sidebar flex flex-col overflow-hidden transition-all duration-200 ease-out",
        isCollapsed ? "w-14" : "w-[280px]"
      )}
    >
      {/* Logo & Collapse */}
      <div
        className={cn(
          "h-14 flex items-center",
          isCollapsed ? "justify-center px-2" : "px-3 justify-between"
        )}
      >
        <button
          className="relative flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            if (isCollapsed) {
              handleToggleCollapse();
            } else {
              navigate("/");
            }
          }}
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
        >
          <img
            src="/dashmote-favicon.png"
            alt="Dashmote"
            className={cn(
              "h-8 w-8 object-contain transition-opacity",
              isCollapsed && logoHovered && "opacity-30"
            )}
          />
          {isCollapsed && logoHovered && (
            <div className="absolute inset-0 h-8 w-8 flex items-center justify-center text-sidebar-foreground">
              <PanelLeft className="h-5 w-5" />
            </div>
          )}
          {!isCollapsed && (
            <span className="ml-2 font-semibold text-sidebar-foreground">DashONE</span>
          )}
        </button>

        {!isCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleCollapse}
            className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent h-8 w-8"
          >
            <PanelLeftClose className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Nav block: persona card and the four sections above the chat list.
          `min-h-0` is what lets it shrink — a flex child defaults to
          `min-height: auto`, so without it these sections hold their full
          natural height, push the chat list and the footer down, and the
          footer leaves the viewport on any window shorter than their sum.
          Shrinking turns into scrolling here rather than clipping. */}
      <div className="min-h-0 overflow-y-auto">
        {/* Persona card — the persona + premise chosen in onboarding, and the
          way to the Account Profile that reconfigures them */}
        {!isChatsExpanded && !isCollapsed && (
          <PersonaCard config={personaVisibility.config} onClick={onAccountProfile} />
        )}

        {/* DASHBOARDS Section — the persona's dashboards only. Browsing the rest
          of the entitlement lives on the home page's Show all. Hidden when
          chats expanded. */}
        {!isChatsExpanded && (
          <>
            <div className={cn("py-2", isCollapsed ? "px-2" : "px-3")}>
              {!isCollapsed && (
                <div className="px-3 pb-1">
                  <span className="text-[12px] font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                    Dashboards
                  </span>
                </div>
              )}
              {personaVisibility.dashboards.inPersona.length > 0 ? (
                renderDashboardItems(isCollapsed, personaVisibility.dashboards.inPersona)
              ) : personaVisibility.status === "loading" ? (
                renderSectionSkeleton(isCollapsed, 3)
              ) : isCollapsed ? null : personaVisibility.status === "error" &&
                personaVisibility.dashboards.notInPersona.length === 0 ? (
                <div className="px-3 py-2 text-[12px] text-sidebar-foreground/50">
                  Couldn&apos;t load dashboards — reload to retry.
                </div>
              ) : (
                <div className="px-3 py-2 text-[12px] text-sidebar-foreground/50">
                  Nothing in your role&apos;s set for this workspace.
                </div>
              )}
            </div>

            <Separator className="bg-sidebar-border" />

            {/* WORKFLOWS Section — the persona's workflows as direct entries;
              clicking deep-links into DashAI's ?agent= capture. */}
            <div className={cn("py-2", isCollapsed ? "px-2" : "px-3")}>
              {!isCollapsed && (
                <div className="px-3 pb-1">
                  <span className="text-[12px] font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                    {workflowsLabel.title}
                  </span>
                </div>
              )}
              {!isCollapsed && personaVisibility.status === "error" && (
                <div className="px-3 py-2 text-[12px] text-sidebar-foreground/50">
                  Couldn&apos;t load {workflowsLabel.noun} — reload to retry.
                </div>
              )}
              {/* max-h-44 (not 56): with the persona card above, a 224px list
                pushes the sidebar's minimum height past short viewports.
                Collapsed, the entries render as icon buttons exactly as the
                dashboards do — dropping them left an empty padded box between
                two separators and no hint that anything was hidden. */}
              {personaVisibility.status !== "error" && (
                <div className={cn("overflow-y-auto", isCollapsed ? "max-h-56" : "max-h-44")}>
                  {personaVisibility.workflows.inPersona.length > 0 ? (
                    renderWorkflowSection(isCollapsed, personaVisibility.workflows.inPersona)
                  ) : personaVisibility.status === "loading" ? (
                    renderSectionSkeleton(isCollapsed, 4)
                  ) : !isCollapsed ? (
                    <div className="px-3 py-2 text-[12px] text-sidebar-foreground/50">
                      Nothing in your role&apos;s set for this workspace.
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <Separator className="bg-sidebar-border" />
          </>
        )}

        {/* DASHHUB Section — Glossary/Videos. Saved Canvas lives in the
          account dropdown. */}
        <div className={cn("py-2", isCollapsed ? "px-2" : "px-3")}>
          {!isCollapsed && (
            <div className="px-3 pb-1">
              <span className="text-[12px] font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                DashHub
              </span>
            </div>
          )}

          {(() => {
            const isActive = location.pathname.startsWith("/learn");
            return (
              <Button
                variant="ghost"
                onClick={() => navigate("/learn")}
                className={cn(
                  "w-full justify-start mt-0.5",
                  isCollapsed ? "h-10 w-10 p-0 justify-center" : "h-10 px-3 gap-3",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-foreground"
                    : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                )}
              >
                <GraduationCap className="h-5 w-5 shrink-0" />
                {!isCollapsed && <span className="text-[15px]">Glossary and Videos</span>}
              </Button>
            );
          })()}
        </div>

        {/* Separator */}
        <Separator className="bg-sidebar-border" />

        {/* DASHAI Section */}
        <div className={cn("py-2", isCollapsed ? "px-2" : "px-3")}>
          {!isCollapsed && (
            <div className="px-3 pb-1">
              <span className="text-[12px] font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                DashAI
              </span>
            </div>
          )}
          {/* New Chat */}
          <Button
            variant="ghost"
            onClick={onNewChat}
            className={cn(
              "w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent mt-0.5",
              isCollapsed ? "h-10 w-10 p-0 justify-center" : "h-10 px-3 gap-3"
            )}
          >
            <Plus className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span className="text-[15px]">New Chat</span>}
          </Button>

          {/* Autonomous Agent */}
          <Button
            variant="ghost"
            onClick={onAutonomousAgent}
            className={cn(
              "w-full justify-start mt-0.5",
              isCollapsed ? "h-10 w-10 p-0 justify-center" : "h-10 px-3 gap-3",
              autonomousAgentOpen
                ? "bg-sidebar-accent text-sidebar-foreground"
                : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
            )}
          >
            <div className="relative">
              <Zap className="h-5 w-5 shrink-0" />
              {autonomousAutoMode && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-green-500 rounded-full animate-pulse" />
              )}
            </div>
            {!isCollapsed && <span className="text-[15px]">Autonomous Agent</span>}
          </Button>
        </div>
      </div>

      {/* Separator */}
      <Separator className="bg-sidebar-border" />

      {/* Chat Sessions List. The floor stops the nav block above from
          squeezing it to nothing on a short window: both shrink, so without
          one the taller sibling wins and the list disappears entirely. */}
      <ScrollArea className="flex-1 min-h-[96px]" onScrollCapture={handleScroll}>
        <div className={cn("py-1 px-2")}>
          {/* Starred Section */}
          {!isCollapsed && starredSessions.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1">
                <span className="text-[12px] font-medium text-sidebar-foreground/50 uppercase tracking-wider">
                  Starred
                </span>
              </div>
              {starredSessions.map((session) => renderChatItem(session))}
              <Separator className="bg-sidebar-border my-1 mx-1" />
            </>
          )}

          {/* Regular Chats Section */}
          {!isCollapsed && (
            <div className="px-3 pt-2 pb-1 flex items-center justify-between">
              <span className="text-[12px] font-medium text-sidebar-foreground/50 uppercase tracking-wider">
                Chats
              </span>
              <div className="flex items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent/80"
                  onClick={toggleChatsExpanded}
                  title={isChatsExpanded ? "Show dashboards" : "Expand chats"}
                  aria-label={isChatsExpanded ? "Show dashboards" : "Expand chats"}
                >
                  {isChatsExpanded ? (
                    <ChevronsDown className="h-3 w-3" />
                  ) : (
                    <ChevronsUp className="h-3 w-3" />
                  )}
                </Button>
                {regularSessions.length > 0 && onDeleteAllChats && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-sidebar-foreground/40 hover:text-destructive hover:bg-sidebar-accent/80"
                    onClick={() => setDeleteAllOpen(true)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          )}
          {!isCollapsed && regularSessions.map((session) => renderChatItem(session))}

          {sessions.length === 0 &&
            !isCollapsed &&
            (sessionsLoading ? (
              <div className="py-8 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-sidebar-foreground/40" />
              </div>
            ) : sessionsError ? (
              <div className="py-8 px-4 flex flex-col items-center gap-2 text-center">
                <p className="text-[13px] text-sidebar-foreground/40">
                  Couldn't load conversations
                </p>
                {onRetrySessions && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[13px] text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/80"
                    onClick={onRetrySessions}
                  >
                    Retry
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-[13px] text-sidebar-foreground/40 text-center py-8 px-4">
                No conversations yet
              </p>
            ))}
          {/* Load more indicator: only meaningful once at least one session is shown */}
          {!isCollapsed && hasMoreSessions && sessions.length > 0 && (
            <div className="py-3 flex items-center justify-center">
              {sessionsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-sidebar-foreground/40" />
              ) : (
                <span className="text-[11px] text-sidebar-foreground/30">Scroll for more</span>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingChatId}
        onOpenChange={(open) => !open && setDeletingChatId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>
              This chat will be removed from your sidebar. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingChatId) onDeleteChat(deletingChatId);
                setDeletingChatId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Chats Confirmation Dialog */}
      <AlertDialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all chats?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all your chats. Starred chats will be kept. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onDeleteAllChats?.();
                setDeleteAllOpen(false);
              }}
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Profile Footer */}
      {onSignOut && (
        <>
          <Separator className="bg-sidebar-border" />
          <div className={cn("p-2", isCollapsed ? "px-1.5" : "px-2")}>
            <UserProfileDropdown
              userName={userName}
              userEmail={userEmail}
              userPhotoUrl={userPhotoUrl}
              allowedClients={allowedClients}
              workspaceListLoaded={workspaceListStatus.loaded}
              workspaceListError={workspaceListStatus.error}
              onRetryWorkspaceList={workspaceListStatus.retry}
              currentClient={currentClient}
              isCollapsed={isCollapsed}
              isAdmin={isAdmin}
              isStaff={isStaff}
              canAccessUserManagement={canAccessUserManagement}
              pinnedWorkspaces={pinnedWorkspaces}
              focusMode={focusMode}
              onTogglePinWorkspace={onTogglePinWorkspace}
              onToggleFocusMode={onToggleFocusMode}
              onClientChange={onClientChange || (() => {})}
              onAccountProfile={onAccountProfile}
              onProfileMemory={onProfileMemory}
              onScheduledReports={onScheduledReports}
              onSavedCanvas={onSavedCanvas}
              onAdminPanel={onAdminPanel}
              onSignOut={onSignOut}
            />
          </div>
        </>
      )}
    </div>
  );

  if (isNarrow) {
    return (
      <>
        {renderSidebar(true)}
        <Sheet open={narrowOverlayOpen} onOpenChange={setNarrowOverlayOpen}>
          <SheetContent side="left" className="w-[260px] p-0 bg-sidebar border-sidebar-border">
            {/* Radix needs a title for the dialog it renders underneath. */}
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            {renderSidebar(false)}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return renderSidebar(isCollapsed);
}
